/**
 * Hook for AI-assisted form filling
 * Simplified version - single prompt and response using dedicated API
 */

import { useState, useCallback } from "react";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types/index";
import type { ExtractedFormData, AIAssistantError } from "../types/ai-assistant";
import { extractFormDataFromLLM } from "../utils/extractFormDataFromLLM";

interface UseAIFormAssistantOptions<T extends BaseEntity = BaseEntity> {
  entityName: string;
  config: SimpleTableConfig<T>;
}

interface UseAIFormAssistantReturn {
  isLoading: boolean;
  error: AIAssistantError | null;
  extractedData: ExtractedFormData | null;
  sendPrompt: (userPrompt: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-assisted form filling
 * Sends user prompt to dedicated form assistance API and extracts form data from response
 */
export function useAIFormAssistant<T extends BaseEntity = BaseEntity>({
  entityName,
  config,
}: UseAIFormAssistantOptions<T>): UseAIFormAssistantReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AIAssistantError | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedFormData | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setExtractedData(null);
    setIsLoading(false);
  }, []);

  const sendPrompt = useCallback(
    async (userPrompt: string) => {
      if (!userPrompt.trim()) {
        setError({
          type: "validation_error",
          message: "Please enter a prompt to get AI assistance.",
        });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Send to dedicated form assistance API
        const response = await fetch("/api/ai/form-assist", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userPrompt,
            entityName,
            formSchema: config,
          }),
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.statusText}`);
        }

        // Read the streamed response
        let fullResponse = "";
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body from API");
        }

        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;

          if (value) {
            const chunk = decoder.decode(value, { stream: true });
            fullResponse += chunk;
          }
        }

        // Extract JSON from response
        // Response format: status messages + "\n✨ Analysis complete!\n\n" + JSON
        let jsonText = fullResponse;
        const jsonStartMarker = "✨ Analysis complete!\n\n";
        const jsonStartIndex = jsonText.indexOf(jsonStartMarker);

        if (jsonStartIndex !== -1) {
          jsonText = jsonText.substring(jsonStartIndex + jsonStartMarker.length).trim();
        }

        if (!jsonText.trim()) {
          setError({
            type: "empty_response",
            message: "LLM returned no data. Please try with a more specific prompt.",
          });
          setIsLoading(false);
          return;
        }

        // Extract form data from LLM response
        const result = extractFormDataFromLLM(jsonText, config);

        if (result.errors.length > 0) {
          setError({
            type: "invalid_json",
            message: result.errors[0],
            details: result.errors,
          });
        } else {
          setExtractedData(result);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error occurred";
        setError({
          type: "api_error",
          message: `Failed to get AI assistance: ${message}`,
          details: err,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [entityName, config]
  );

  return {
    isLoading,
    error,
    extractedData,
    sendPrompt,
    clearError,
    reset,
  };
}
